import { motion } from "framer-motion";
import { ShieldCheck, Smartphone } from "lucide-react";
import NetworkCanvas from "@/components/NetworkCanvas";
import CTABand from "@/components/CTABand";
import ServiceCard from "@/components/ServiceCard";
import { Reveal, SectionHead, ease } from "@/components/Reveal";
import { COMPANY, CYBERSECURITY_SERVICES, MOBILE_PRIVACY_SERVICES } from "@/data/content";

export default function Services() {
  return (
    <main>
      {/* Hero */}
      <section className="noise relative overflow-hidden bg-midnight" data-testid="services-hero">
        <NetworkCanvas className="absolute inset-0 h-full w-full opacity-40" density={30000} interactive={false} />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-midnight/60 via-transparent to-midnight" />
        <div className="relative mx-auto w-full max-w-7xl px-6 pb-24 pt-40 lg:px-8 lg:pb-32 lg:pt-48">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease }}
            className="flex items-center gap-4"
          >
            <span className="h-px w-12 bg-gradient-to-r from-iris to-aqua" />
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-aqua">Our Services</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.12, ease }}
            className="mt-8 font-display text-4xl font-extrabold leading-[1.05] tracking-tighter text-white sm:text-5xl lg:text-6xl"
            data-testid="services-hero-title"
          >
            Cybersecurity <span className="text-gradient">Services</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25, ease }}
            className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
            data-testid="services-hero-tagline"
          >
            {COMPANY.tagline}
          </motion.p>
        </div>
      </section>

      {/* CyberSecurity category */}
      <section className="bg-offwhite" data-testid="services-cybersecurity-section">
        <div className="mx-auto w-full max-w-7xl px-6 py-24 lg:px-8 lg:py-32">
          <SectionHead num="01" eyebrow="Service Category" title="CyberSecurity" />
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3" data-testid="services-cybersecurity-grid">
            {CYBERSECURITY_SERVICES.map((service, i) => (
              <Reveal key={service.num} delay={i * 0.1} className="h-full">
                <ServiceCard service={service} to="/contact-us" testIdPrefix="services-card" linkTestIdSuffix="learn-more" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile Privacy Protection category */}
      <section className="bg-white" data-testid="services-mobile-section">
        <div className="mx-auto w-full max-w-7xl px-6 py-24 lg:px-8 lg:py-32">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <SectionHead num="02" eyebrow="Service Category" title="Mobile Privacy Protection" />
            <Reveal delay={0.15}>
              <div className="flex items-center gap-4 rounded-2xl border border-hairline bg-offwhite p-5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-iris text-white">
                  <Smartphone className="h-5 w-5" />
                </span>
                <p className="max-w-sm text-sm leading-relaxed text-steel">
                  Cutting-edge cybersecurity solutions tailored for portable devices, including Android and iOS
                  platforms.
                </p>
              </div>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3" data-testid="services-mobile-grid">
            {MOBILE_PRIVACY_SERVICES.map((service, i) => (
              <Reveal key={service.num} delay={i * 0.1} className="h-full">
                <ServiceCard service={service} to="/contact-us" testIdPrefix="services-card" linkTestIdSuffix="learn-more" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </main>
  );
}
